import { WaterProvider } from "../misc";
import _ from "lodash";
import { BaseTaxonomy, LongText } from "./graphQL";

export type Unit =
    | "%"
    | "$"
    | "$,000"
    | "$/annum"
    | "$/connection"
    | "$/ML"
    | "Count"
    | "ML"
    | "kL"
    | "000s"
    | "TariffStructure"
    | "per 100 km water main"
    | "per 100 km sewer main"
    | "per 1000 connections";

export const isUnitDollar = (unit: Unit) => {
    return unit === "$" || unit === "$,000" || unit === "$/annum" || unit === "$/ML" || unit === "$/connection";
};

export const isUnitPercentage = (unit: Unit) => {
    return unit === "%";
};

export const isUnitCount = (unit: Unit) => {
    return unit === "Count";
};

export const isWaterMainbreak = (unit: Unit) => {
    return unit === "per 100 km water main" || unit === "per 100 km sewer main";
};

export const isUnplannedInterruptions = (unit: Unit) => {
    return unit === "per 1000 connections";
};

export const isUnitMultiply = (unit: Unit) => {
    return unit === "000s";
};

export const getValue = (value: number, unit: Unit) => {
    if (unit === "000s") return value * 1000;
    if (unit === "$,000") return value * 1000;

    return value;
};

export const getBaseValue = (value: number, unit: Unit): { value: number; unit: Unit } => {
    if (unit === "000s")
        return {
            value: value == null ? null : value * 1000,
            unit: "Count"
        };
    if (unit === "$,000")
        return {
            value: value == null ? null : value * 1000,
            unit: "$"
        };
    if (unit === "ML")
        return {
            value: value == null ? null : value * 1000,
            unit: "kL"
        };

    if (unit === "kL")
        return {
            value: value == null ? null : value,
            unit: "kL"
        };

    return { value, unit };
};

export const unitTooltip = (unit: Unit) => {
    return unit == null
        ? ""
        : isUnitDollar(unit) && unit !== "$,000"
        ? `(${unit})`
        : unit === "$,000"
        ? `($)`
        : isUnitPercentage(unit)
        ? "(%)"
        : isUnitMultiply(unit)
        ? ""
        : unit.indexOf("per") > -1
        ? ""
        : `(${unit})`;
};

export const roundValue = (value: number, unit: Unit, consistentUnit = false, unitTransform = true) => {
    if (value == null)
        return {
            value,
            unit,
            decimals: 0
        };
    let decimals = 0;

    if (isUnitDollar(unit)) {
        let decimals = 2;
        if (unit === "$,000") decimals = 0;

        return {
            value: _.round(getValue(value, unit), 2),
            decimals,
            unit: "$"
        };
    }

    if (isUnitPercentage(unit)) {
        decimals = 1;
    }

    if (isWaterMainbreak(unit)) {
        decimals = 1;
    }

    if (isUnplannedInterruptions(unit)) {
        decimals = 1;
    }

    if (unit === "kL" && unitTransform) {
        if (!consistentUnit && value >= 1000) {
            unit = "ML";
            value = value / 1000;
            decimals = 0;
        } else if (!consistentUnit && value < 1000) {
            // unit = "kL";
            // value = value;
            decimals = Math.abs(value) < 1 ? 2 : 0;
        } else if (consistentUnit) {
            unit = "ML";
            value = value / 1000;
            decimals = value >= 1000 * 10 ? 0 : 2;
        }
    }

    return {
        value: _.round(getValue(value, unit), decimals),
        unit,
        decimals: decimals
    };
};

export const prefixValue = (value, unit?: Unit) => {
    return unit == null ? undefined : isUnitDollar(unit) ? "$" : undefined;
};

export const suffixValue = (value, unit?: Unit) => {
    return unit == null
        ? undefined
        : !isUnitDollar(unit) && !isUnitCount(unit) && !isUnitMultiply(unit)
        ? unit.indexOf("per") > -1
            ? ""
            : unit
        : undefined;
};

export const deltaValue = (prevValue?: number, latestValue?: number, unit?: Unit) => {
    if (unit === "%") {
        return latestValue == null ? null : prevValue == null ? null : _.round(latestValue - prevValue, 1);
    }

    const delta =
        latestValue == null
            ? null
            : prevValue === latestValue
            ? 0
            : prevValue == null
            ? null
            : prevValue === 0
            ? 100
            : _.round(((latestValue - prevValue) / Math.abs(prevValue)) * 100, 1);

    return delta;
};

export const NDValueStatusDomain = {
    NR: 1,
    MD: 1,
    RD: 1
};

export type ValueStatus = "NR" | "MD" | "RD" | "yes" | "no" | "high" | "fair" | "unsure" | "low" | "very low";
export interface BaseKpiReportEntity extends BaseTaxonomy {
    unit?: Unit;
    indicatorTitle?: string;
    value?: number;
    valueStatus?: ValueStatus;
    year: number;
    kpiCode: string;
    schemeId: string;
    schemeType: string;
    scheme?: string;
    comment?: LongText;
    organisation?: string;
}

export const ValueStatusDomain = {
    NR: 1,
    MD: 1,
    RD: 1,
    yes: 1,
    no: 1,
    high: 1,
    fair: 1,
    unsure: 1,
    low: 1,
    "very low": 1
};

export interface KpiReportEntity extends BaseKpiReportEntity {
    provider?: {
        entity: WaterProvider;
    };
}

export interface KpiReport extends BaseKpiReportEntity {
    provider?: WaterProvider;
}
